import { parse } from 'date-fns'
import type { PricePoint } from '@/services/api/priceApi'

// Parses SMARD download CSV format (semicolon-delimited, local time, €/MWh).
// Column layout:  0=Start date, 1=End date, 2=Germany/Luxembourg [€/MWh], ...
// Date format:    "Jan 1, 2025 12:00 AM"
// Timestamps are interpreted as local time — the app is expected to run in CET/CEST.

const DATE_FORMAT = 'MMM d, yyyy hh:mm a'
const IDA_DATE_FORMAT = 'dd.MM.yyyy HH:mm:ss'

// Parses the combined EPEX Spot CSV format.
// Format: comma-delimited, Europe/Berlin local timestamps ("2025-01-01 00:00:00"), values in €/kWh.
// Columns: start_time, DA, ID-A, ID-Cmin, ID-Cmax, ID-C1, ID-C3, ID-Cfull
// Returns { da, idaSeries } where idaSeries keys match column names (ID-A, ID-Cmin, etc.)

// Converts a naive "YYYY-MM-DD HH:mm:ss" timestamp in Europe/Berlin to UTC epoch ms.
// Works by parsing as UTC first, then computing the Berlin offset at that moment and correcting.
// Iterates once because DST transitions are rare and the first correction is accurate in all
// non-transition edge cases; the second parse confirms and handles the ambiguous clock-back hour.
function berlinToUtcMs(rawTs: string): number {
  const naive = Date.parse(rawTs + 'Z')
  if (isNaN(naive)) return NaN
  // Subtract Berlin offset to convert Berlin local time → UTC.
  // First pass: use the offset at the naive UTC time as an approximation.
  const corrected = naive - berlinOffsetMs(naive)
  // Second pass: re-evaluate the offset at the corrected time to handle the
  // clock-back DST hour where the same wall time occurs twice.
  return naive - berlinOffsetMs(corrected)
}

// Returns the Europe/Berlin UTC offset in ms (positive = ahead of UTC, e.g. CET = +3600000).
function berlinOffsetMs(epochMs: number): number {
  // Format the epoch as UTC parts then as Berlin parts; difference = offset
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(new Date(epochMs))
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)!.value)
  const berlinUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'))
  return berlinUtc - epochMs
}

export function parseEpexCombinedCsv(text: string): { da: PricePoint[]; idaSeries: Record<string, PricePoint[]> } {
  const lines = text.split('\n')
  const headerCols = lines[0].split(',').map((h) => h.trim())

  const daColIndex = headerCols.indexOf('DA')
  const idaCols: { colIndex: number; key: string }[] = []
  for (let ci = 0; ci < headerCols.length; ci++) {
    const h = headerCols[ci]
    if (h.startsWith('ID-') || h === 'ID-A') idaCols.push({ colIndex: ci, key: h })
  }

  const da: PricePoint[] = []
  const idaSeries: Record<string, PricePoint[]> = Object.fromEntries(idaCols.map(({ key }) => [key, []]))

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    const cols = line.split(',')
    if (cols.length < 2) continue

    const rawTs = cols[0].trim()
    if (!rawTs) continue
    const timestampMs = berlinToUtcMs(rawTs)
    if (isNaN(timestampMs)) continue

    if (daColIndex >= 0) {
      const price = parseFloat(cols[daColIndex])
      if (!isNaN(price)) da.push({ timestampMs, priceEurKwh: price })
    }

    for (const { colIndex, key } of idaCols) {
      const price = parseFloat(cols[colIndex] ?? '')
      if (!isNaN(price)) idaSeries[key].push({ timestampMs, priceEurKwh: price })
    }
  }

  da.sort((a, b) => a.timestampMs - b.timestampMs)
  for (const key of Object.keys(idaSeries)) idaSeries[key].sort((a, b) => a.timestampMs - b.timestampMs)

  return { da, idaSeries }
}

// Quote-aware CSV splitter for comma-delimited files with European decimal notation.
// Fields containing commas as decimal separators are wrapped in double quotes.
function splitCsvLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuote = false
  for (const ch of line) {
    if (ch === '"') { inQuote = !inQuote }
    else if (ch === ',' && !inQuote) { fields.push(current); current = '' }
    else { current += ch }
  }
  fields.push(current)
  return fields
}

export function parseSmardCsv(text: string): PricePoint[] {
  const lines = text.split('\n')
  const points: PricePoint[] = []
  const ref = new Date(0)

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    const cols = line.split(';')
    if (cols.length < 3) continue

    const rawDate = cols[0].trim()
    const rawPrice = cols[2].trim()

    if (!rawDate || rawPrice === '-' || rawPrice === '') continue

    const parsed = parse(rawDate, DATE_FORMAT, ref)
    if (isNaN(parsed.getTime())) continue

    const priceEurMwh = parseFloat(rawPrice.replace(',', '.'))
    if (isNaN(priceEurMwh)) continue

    points.push({ timestampMs: parsed.getTime(), priceEurKwh: priceEurMwh / 1000 })
  }

  return points.sort((a, b) => a.timestampMs - b.timestampMs)
}

// Parses IDA (Intraday Auction) CSV format (local time, €/MWh).
// Supports two delimiter styles:
//   - Semicolon-delimited (e.g. "Germany Price Analysis" export): unquoted comma decimals
//   - Comma-delimited (e.g. ida-prices-de-2025.csv): quoted comma decimal fields
// Column layout:  0=Date, 1=DateTime (DD.MM.YYYY HH:mm:ss), 2=DeliveryStart, ...
// ID price columns are detected dynamically: any column whose header contains "(€/MWh)"
// and whose stripped name starts with "ID" (e.g. "IDFull", "ID1", "ID3").
// Returns a map of series key → sorted PricePoint[].
export function parseIdaCsv(text: string): Record<string, PricePoint[]> {
  const lines = text.split('\n')
  const ref = new Date(0)

  // Detect delimiter from the header line
  const headerLine = lines[0] ?? ''
  const semicolonDelimited = headerLine.startsWith('Date;')

  const headerCols = semicolonDelimited ? headerLine.split(';') : splitCsvLine(headerLine)

  // Detect all ID* (€/MWh) columns from the header
  const seriesColumns: { colIndex: number; key: string }[] = []
  for (let ci = 0; ci < headerCols.length; ci++) {
    const h = headerCols[ci].trim()
    if (!h.includes('(€/MWh)')) continue
    const key = h.replace('(€/MWh)', '').trim()
    if (!key.startsWith('ID') || key === '') continue
    seriesColumns.push({ colIndex: ci, key })
  }

  // For the bundled comma-delimited file that lacks (€/MWh) annotations, fall back to
  // fixed columns 5 (IDFull) and 6 (ID1) when no annotated columns are detected.
  const columns =
    seriesColumns.length > 0
      ? seriesColumns
      : [
          { colIndex: 5, key: 'IDFull' },
          { colIndex: 6, key: 'ID1' },
        ]

  const seriesMap: Record<string, PricePoint[]> = {}
  for (const { key } of columns) seriesMap[key] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    const cols = semicolonDelimited ? line.split(';') : splitCsvLine(line)
    if (cols.length < 2) continue

    const rawDate = cols[1].trim()
    if (!rawDate) continue

    const parsed = parse(rawDate, IDA_DATE_FORMAT, ref)
    if (isNaN(parsed.getTime())) continue

    for (const { colIndex, key } of columns) {
      const raw = (cols[colIndex] ?? '').trim().replace(',', '.')
      const price = parseFloat(raw)
      if (!isNaN(price)) seriesMap[key].push({ timestampMs: parsed.getTime(), priceEurKwh: price / 1000 })
    }
  }

  return Object.fromEntries(
    Object.entries(seriesMap).map(([k, pts]) => [k, pts.sort((a, b) => a.timestampMs - b.timestampMs)])
  )
}
